import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles"; // 🎨 Added for Theme Compatibility
import { generateAttendancePdf } from "./AttendancePdfHelper"; 
import PrintIcon from "@mui/icons-material/Print";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import { GetEmployees, GetAttendanceMonthlyHistory } from "../../../Services/Services";

const months = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 6 }, (_, i) => currentYear - i + 1);

export default function AttendanceReport() {
  const theme = useTheme(); // 🎨 Hook to access global theme

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(9);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [activeDivisionName, setActiveDivisionName] = useState("");
  const [debugInfo, setDebugInfo] = useState("");

  const getEmployeeName = (emp) => {
    if (!emp) return "Unknown Employee";
    if (emp.name) return emp.name;
    if (emp.full_name) return emp.full_name;
    if (emp.employee_name) return emp.employee_name;
    if (emp.emp_name) return emp.emp_name;
    if (emp.first_name || emp.last_name)
      return `${emp.first_name || ""} ${emp.last_name || ""}`.trim();
    if (emp.user && typeof emp.user === "object") {
      return (
        `${emp.user.first_name || ""} ${emp.user.last_name || ""}`.trim() ||
        emp.user.username ||
        ""
      );
    }
    return `Employee #${emp.id}`;
  };

  const getEmployeeCode = (emp) => {
    if (!emp) return "N/A";
    return (
      emp.emp_code ||
      emp.employee_code ||
      emp.unique_id ||
      emp.code ||
      emp.emp_id ||
      `ID: ${emp.id}`
    );
  };

 

  useEffect(() => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      if (stored) {
        const parsed = JSON.parse(stored);
        setActiveDivisionName(parsed?.division || "Default Division");
      }
    } catch (e) {}
    fetchEmployeesList();
  }, []);

  const fetchEmployeesList = async () => {
    try {
      const response = await GetEmployees();
      const dataList = Array.isArray(response)
        ? response
        : response?.results || response?.data || response?.employees || [];
      setEmployees(dataList);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  const normalizeStatus = (status) => {
    const s = String(status || "").trim().toLowerCase();
    if (["present", "p", "checked_in", "checked-in", "checkin", "in"].includes(s))
      return "Present";
    if (["checked_out", "checked-out", "checkout", "out", "completed"].includes(s))
      return "Present";
    if (["absent", "a"].includes(s)) return "Absent";
    if (["leave", "l", "on_leave"].includes(s)) return "Leave";
    if (["holiday", "h", "weekly_off", "weekoff", "off"].includes(s)) return "Holiday";
    return status || "N/A";
  };

  const getStatusChip = (status) => {
    const s = normalizeStatus(status);
    if (s === "Present")
      return <Chip label="Present" color="success" size="small" variant="filled" sx={{ fontWeight: 600, borderRadius: 1.5 }} />;
    if (s === "Absent")
      return <Chip label="Absent" color="error" size="small" variant="filled" sx={{ fontWeight: 600, borderRadius: 1.5 }} />;
    if (s === "Leave")
      return <Chip label="Leave" color="warning" size="small" variant="filled" sx={{ fontWeight: 600, borderRadius: 1.5 }} />;
    if (s === "Holiday")
      return <Chip label="Holiday" color="info" size="small" variant="filled" sx={{ fontWeight: 600, borderRadius: 1.5 }} />;
    return <Chip label={String(status || "N/A")} size="small" sx={{ borderRadius: 1.5 }} />;
  };

  const extractTime = (value) => {
    if (!value || value === "-" || value === "null" || value === "None") return null;
    try {
      if (/^\d{1,2}:\d{2}/.test(String(value))) {
        const [h, m] = String(value).split(":");
        return { h: parseInt(h, 10), m: parseInt(m, 10) };
      }
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return { h: d.getHours(), m: d.getMinutes() };
      }
      const match = String(value).match(/(\d{1,2}):(\d{2})/);
      if (match) return { h: parseInt(match[1], 10), m: parseInt(match[2], 10) };
    } catch {}
    return null;
  };

  const formatTime = (value) => {
    const t = extractTime(value);
    if (!t) return "-";
    const ampm = t.h >= 12 ? "PM" : "AM";
    const hr = t.h % 12 || 12;
    return `${String(hr).padStart(2, "0")}:${String(t.m).padStart(2, "0")} ${ampm}`;
  };

  const calculateTotalHours = (inValue, outValue) => {
    const a = extractTime(inValue);
    const b = extractTime(outValue);
    if (!a || !b) return "-";
    const inMins = a.h * 60 + a.m;
    const outMins = b.h * 60 + b.m;
    let diff = outMins - inMins;
    if (diff < 0) diff += 24 * 60;
    const hrs = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hrs} hrs ${mins} mins`;
  };

  const extractRecords = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.records)) return data.records;
    if (Array.isArray(data.attendance)) return data.attendance;
    if (Array.isArray(data.history)) return data.history;
    if (data.data && Array.isArray(data.data.results)) return data.data.results;
    return [];
  };

  const handleFetchReport = async () => {
    if (!selectedEmployee) {
      alert("Please select an employee!");
      return;
    }

    setLoading(true);
    setDebugInfo("");
    try {
      const emp = employees.find((e) => String(e.id) === String(selectedEmployee));

      let queryParams = `employee_id=${selectedEmployee}&month=${selectedMonth}&year=${selectedYear}`;
      let data = await GetAttendanceMonthlyHistory(queryParams);
      let recordList = extractRecords(data);

      if (recordList.length === 0 && emp) {
        const code = emp.unique_id || emp.emp_code || emp.employee_code;
        if (code) {
          queryParams = `employee_id=${encodeURIComponent(code)}&month=${selectedMonth}&year=${selectedYear}`;
          data = await GetAttendanceMonthlyHistory(queryParams);
          recordList = extractRecords(data);
        }
      }

      let presentCount = 0;
      let absentCount = 0;
      let leaveCount = 0;
      let holidayCount = 0;

      const formattedRecords = recordList
        .map((record) => {
          const rawStatus = record.status || record.attendance_status || "";
          const status = normalizeStatus(rawStatus);

          if (status === "Present") presentCount++;
          else if (status === "Absent") absentCount++;
          else if (status === "Leave") leaveCount++;
          else if (status === "Holiday") holidayCount++;

          const dateVal = record.date || record.attendance_date || (record.in_time ? String(record.in_time).slice(0, 10) : null);
          let dayName = record.day_name || record.day || "";
          if (!dayName && dateVal) {
            const dt = new Date(dateVal);
            if (!isNaN(dt.getTime())) {
              dayName = dt.toLocaleDateString("en-US", { weekday: "short" });
            }
          }

          return {
            date: dateVal || "N/A",
            day: dayName || "-",
            status,
            rawStatus,
            inTime: record.in_time || record.check_in || record.punch_in || null,
            outTime: record.out_time || record.check_out || record.punch_out || null,
            remarks: record.remarks || record.note || record.comment || "-",
          };
        })
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));

      setReportData({
        employee: emp,
        month: months.find((m) => m.value === selectedMonth)?.label,
        year: selectedYear,
        summary: {
          totalDays: formattedRecords.length,
          present: presentCount,
          absent: absentCount,
          leave: leaveCount,
          holiday: holidayCount,
        },
        records: formattedRecords,
      });
    } catch (err) {
      console.error("Error generating report:", err);
      setDebugInfo(`API Error: ${err?.response?.status || ""} ${err?.response?.data?.error || err.message || "Failed"}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    generateAttendancePdf(reportData, activeDivisionName);
  };

  // 🎨 Dashboard Theme Summary Cards Array
  const summaryCards = reportData ? [
    { label: "Total Days", value: reportData.summary.totalDays, color: theme.palette.primary },
    { label: "Present", value: reportData.summary.present, color: theme.palette.success },
    { label: "Absent", value: reportData.summary.absent, color: theme.palette.error },
    { label: "Leaves", value: reportData.summary.leave, color: theme.palette.warning },
    { label: "Holidays", value: reportData.summary.holiday, color: theme.palette.info },
  ] : [];

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      <style>
        {`
          @media print {
            body * { visibility: hidden; }
            #printable-area, #printable-area * { visibility: visible; }
            #printable-area { position: absolute; left: 0; top: 0; width: 100%; color: #000 !important; }
            .no-print { display: none !important; }
            .print-avoid-break { page-break-inside: avoid; }
          }
        `}
      </style>

      {/* 🎨 Theme Styled Header */}
      <Box className="no-print" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentOutlinedIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
            Attendance Report
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Generate, review, and print monthly employee attendance sheets.
          </Typography>
        </Box>
      </Box>

      {/* 🎨 Theme Styled Filter Card */}
      <Card elevation={2} className="no-print" sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Employee</InputLabel>
                <Select
                  value={selectedEmployee}
                  label="Select Employee"
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  sx={{ borderRadius: 1.5 }}
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {getEmployeeName(emp)} ({getEmployeeCode(emp)})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Select Month"
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  sx={{ borderRadius: 1.5 }}
                >
                  {months.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Select Year"
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  sx={{ borderRadius: 1.5 }}
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1.5} width="100%">
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<FilterAltIcon />}
                  onClick={handleFetchReport}
                  disabled={loading}
                  sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : "Fetch"}
                </Button>
                {reportData && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
                  >
                    Print
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>

          {debugInfo && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>
              {debugInfo}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Main Report Area */}
      {reportData ? (
        <Box id="printable-area">
          <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, bgcolor: "background.paper" }}>
            
            {/* 🎨 Theme Styled Report Header */}
            <Box sx={{ borderBottom: `2px solid ${theme.palette.divider}`, pb: 2, mb: 4 }}>
              <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                <Grid item xs={12} sm={7}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <BusinessIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary", letterSpacing: 0.5 }}>
                      {activeDivisionName.toUpperCase()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BadgeIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {getEmployeeName(reportData.employee)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: "text.secondary", ml: 3.5, display: 'block' }}>
                    Emp Code: <b>{getEmployeeCode(reportData.employee)}</b> 
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={5} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "primary.main" }}>
                    {reportData.month} {reportData.year}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Generated on: {new Date().toLocaleDateString("en-GB")}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* 🎨 Theme Aware Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 4 }} className="print-avoid-break">
              {summaryCards.map((card, idx) => (
                <Grid item xs={6} sm={2.4} key={idx}>
                  <Card 
                    elevation={0} 
                    sx={{ 
                      textAlign: "center", 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: alpha(card.color.main, 0.08),
                      border: `1px solid ${alpha(card.color.main, 0.2)}`
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, color: card.color.dark, textTransform: 'uppercase' }}>
                      {card.label}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: card.color.main, mt: 0.5 }}>
                      {card.value}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* 🎨 Theme Styled Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', py: 1.5 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Day</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>In Time</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Out Time</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Total Hours</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.records.length > 0 ? (
                    reportData.records.map((row, idx) => (
                      <TableRow key={idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ py: 1.5, color: 'text.secondary', fontWeight: 500 }}>{row.date}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{row.day}</TableCell>
                        <TableCell>{getStatusChip(row.status)}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{formatTime(row.inTime)}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{formatTime(row.outTime)}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {calculateTotalHours(row.inTime, row.outTime)}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{row.remarks}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Typography variant="body1" color="text.secondary">
                          No attendance records found for the selected period.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

          </Paper>
        </Box>
      ) : (
        /* 🎨 Theme Styled Empty State */
        <Paper
          elevation={1}
          className="no-print"
          sx={{
            py: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 3,
            bgcolor: alpha(theme.palette.background.default, 0.5),
          }}
        >
          <CalendarMonthIcon sx={{ fontSize: 70, color: alpha(theme.palette.text.disabled, 0.5), mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
            No Report Generated Yet
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
            Select an employee and period above to generate the attendance report.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}